import { useMemo, useState, useCallback } from 'react'
import type { AuthSession } from '../../../../types/plans'
import {
  misImportedPlanComposeTimeLabel,
  misImportedPlanDateLabel,
  misImportedPlanDoctorLabel,
  isAiGeneratedMisPlan,
  misImportedPlanTitleLabel,
  misImportedServicesFromPlan,
  selectedServiceKey,
  type MisImportedPlanSummary,
  type SelectedPlanService,
} from './selectedPlanServices'

export function MisImportedPlansSidebar({
  apiBase,
  session,
  summaries,
  selectedServices,
  onSelectionChange,
}: {
  apiBase: string
  session: AuthSession
  summaries: MisImportedPlanSummary[]
  selectedServices: SelectedPlanService[]
  onSelectionChange: (services: SelectedPlanService[]) => void
}) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const [cache, setCache] = useState<Record<string, SelectedPlanService[]>>({})
  const [loading, setLoading] = useState<Set<string>>(new Set())

  const selectedKeys = useMemo(
    () => new Set(selectedServices.map(selectedServiceKey)),
    [selectedServices],
  )

  const loadPlan = useCallback(async (planId: string): Promise<SelectedPlanService[]> => {
    if (cache[planId]) return cache[planId]
    if (loading.has(planId)) return []
    setLoading((prev) => new Set(prev).add(planId))
    try {
      const res = await fetch(`${apiBase}/staff/mis/treatment-plans/${planId}`, {
        headers: { Authorization: `Bearer ${session.accessToken}` },
      })
      if (!res.ok) return []
      const data = (await res.json()) as { plan?: { plan_id: string; stages?: unknown[] } }
      if (!data.plan) return []
      const services = misImportedServicesFromPlan(
        data.plan as Parameters<typeof misImportedServicesFromPlan>[0],
      )
      setCache((prev) => ({ ...prev, [planId]: services }))
      return services
    } finally {
      setLoading((prev) => {
        const next = new Set(prev)
        next.delete(planId)
        return next
      })
    }
  }, [apiBase, cache, loading, session.accessToken])

  const togglePlan = async (planId: string) => {
    const next = new Set(expanded)
    if (next.has(planId)) {
      next.delete(planId)
    } else {
      next.add(planId)
      await loadPlan(planId)
    }
    setExpanded(next)
  }

  const toggleService = (service: SelectedPlanService) => {
    const key = selectedServiceKey(service)
    if (selectedKeys.has(key)) {
      onSelectionChange(selectedServices.filter((s) => selectedServiceKey(s) !== key))
    } else {
      onSelectionChange([...selectedServices, service])
    }
  }

  const setPlanSelection = async (planId: string, checked: boolean) => {
    const planItems = await loadPlan(planId)
    if (checked) {
      const merged = [...selectedServices]
      for (const item of planItems) {
        if (!selectedKeys.has(selectedServiceKey(item))) merged.push(item)
      }
      onSelectionChange(merged)
    } else {
      const planKeys = new Set(planItems.map(selectedServiceKey))
      onSelectionChange(selectedServices.filter((s) => !planKeys.has(selectedServiceKey(s))))
    }
  }

  if (summaries.length === 0) {
    return (
      <p className="rounded border border-dashed border-slate-200 bg-slate-50 p-2 text-[11px] text-slate-500">
        Нет импортированных MIS-планов у пациента (или MIS не настроен).
      </p>
    )
  }

  return (
    <div className="max-h-48 overflow-y-auto rounded border border-slate-200 bg-white text-[11px]">
      {summaries.map((plan) => {
        const planId = plan.plan_id
        const isOpen = expanded.has(planId)
        const services = cache[planId] ?? []
        const isLoading = loading.has(planId)
        const composeTime = misImportedPlanComposeTimeLabel(plan)
        const isAi = isAiGeneratedMisPlan(plan)
        return (
          <div key={planId} className="border-b border-slate-100 last:border-0">
            <div className="flex items-center gap-1 px-2 py-1">
              <input
                type="checkbox"
                className="h-3 w-3"
                onChange={(e) => void setPlanSelection(planId, e.target.checked)}
              />
              <button
                type="button"
                className="flex min-w-0 flex-1 items-center gap-1 text-left"
                onClick={() => void togglePlan(planId)}
              >
                <span className={`text-slate-400 ${isOpen ? 'rotate-90' : ''}`}>›</span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-medium text-slate-700">
                    {misImportedPlanTitleLabel(plan)}
                  </span>
                  <span className="block truncate text-[10px] text-slate-500">
                    {misImportedPlanDateLabel(plan)} · {misImportedPlanDoctorLabel(plan)}
                  </span>
                </span>
              </button>
            </div>
            {isOpen ? (
              <div className="bg-slate-50 px-2 pb-1">
                {isLoading ? (
                  <p className="py-1 text-slate-400">Загрузка…</p>
                ) : services.length === 0 ? (
                  <p className="py-1 text-slate-400">Нет позиций</p>
                ) : (
                  services.map((svc) => (
                    <label key={selectedServiceKey(svc)} className="flex cursor-pointer items-center gap-2 py-0.5">
                      <input
                        type="checkbox"
                        checked={selectedKeys.has(selectedServiceKey(svc))}
                        onChange={() => toggleService(svc)}
                        className="h-3 w-3"
                      />
                      <span className="truncate text-slate-700">
                        {svc.display_name}
                        {svc.tooth ? ` · ${svc.tooth}` : ''}
                      </span>
                    </label>
                  ))
                )}
              </div>
            ) : null}
          </div>
        )
      })}
    </div>
  )
}
